import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import DealDetailPanel from './components/pipeline/DealDetailPanel';
import PipelineBoard from './components/pipeline/PipelineBoard';
import { DealFormModal, ActivityFormModal, EmailDetailModal, UnmatchedEmailsModal, EmailComposeModal } from './components/pipeline/PipelineModals';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';
import './Pipeline.css';

function Pipeline() {
  const [pipelineData, setPipelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDealForm, setShowDealForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealActivities, setDealActivities] = useState([]);
  const [dealEmails, setDealEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  // Omatchade mejl
  const [showUnmatchedEmails, setShowUnmatchedEmails] = useState(false);
  const [unmatchedEmails, setUnmatchedEmails] = useState([]);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [creatingLead, setCreatingLead] = useState(null);

  // AI-sammanfattningar
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Företag och kontakter för formulär
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [users, setUsers] = useState([]);

  // Filter state
  const [filters, setFilters] = useState({
    assignedToId: '',
    companyId: '',
    minValue: '',
    maxValue: '',
    minProbability: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Bulk selection state
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);

  // Formulärdata för deal
  const [formData, setFormData] = useState({
    name: '',
    b2bCompanyId: '',
    contactId: '',
    stageId: '',
    courseType: 'custom',
    customCourseDescription: '',
    estimatedParticipants: '',
    preferredLocation: '',
    preferredDates: '',
    expectedValue: '',
    probability: 50,
    expectedCloseDate: '',
    source: '',
    notes: ''
  });

  // Aktivitetsformulär
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityFormData, setActivityFormData] = useState({
    activityTypeId: '',
    subject: '',
    description: '',
    scheduledAt: ''
  });

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    to: '',
    subject: '',
    body: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  const config = { withCredentials: true };

  // Hämta pipeline-data med filter
  const fetchPipeline = useCallback(async (currentFilters = filters) => {
    try {
      setError('');
      // Build query params from filters
      const params = new URLSearchParams();
      if (currentFilters.assignedToId) params.append('assignedToId', currentFilters.assignedToId);
      if (currentFilters.companyId) params.append('companyId', currentFilters.companyId);
      if (currentFilters.minValue) params.append('minValue', currentFilters.minValue);
      if (currentFilters.maxValue) params.append('maxValue', currentFilters.maxValue);
      if (currentFilters.minProbability) params.append('minProbability', currentFilters.minProbability);
      if (currentFilters.search) params.append('search', currentFilters.search);

      const url = params.toString() ? `${API_URL}/deals/pipeline?${params}` : `${API_URL}/deals/pipeline`;
      const response = await axios.get(url, config);
      setPipelineData(response.data.data || []);
      setLoading(false);
      setSelectedDeals([]); // Clear selection when data changes
    } catch (error) {
      console.error('Fel vid hämtning av pipeline:', error);
      setError('Kunde inte hämta pipeline-data');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Hämta användare (behövs för filter — laddas vid mount)
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/users`, config);
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Fel vid hämtning av användare:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hämta formulärdata (företag, kontakter, aktivitetstyper) — lazy, cacheas i state
  const [formDataLoaded, setFormDataLoaded] = useState(false);
  const ensureFormData = useCallback(async () => {
    if (formDataLoaded) return;
    try {
      const promises = [];
      if (companies.length === 0) promises.push(axios.get(`${API_URL}/b2b-companies`, config));
      if (contacts.length === 0) promises.push(axios.get(`${API_URL}/contacts`, config));
      if (activityTypes.length === 0) promises.push(axios.get(`${API_URL}/activity-types`, config));

      if (promises.length === 0) { setFormDataLoaded(true); return; }

      const results = await Promise.all(promises);
      let idx = 0;
      if (companies.length === 0) setCompanies(results[idx++].data.data || []);
      if (contacts.length === 0) setContacts(results[idx++].data.data || []);
      if (activityTypes.length === 0) setActivityTypes(results[idx++].data.data || []);
      setFormDataLoaded(true);
    } catch (error) {
      console.error('Fel vid hämtning av formulärdata:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDataLoaded, companies.length, contacts.length, activityTypes.length]);

  useEffect(() => {
    fetchPipeline();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPipeline, fetchUsers]);

  // Hämta aktiviteter för en deal
  const fetchDealActivities = async (dealId) => {
    try {
      const response = await axios.get(`${API_URL}/deals/${dealId}/activities`, config);
      setDealActivities(response.data.data || []);
    } catch (error) {
      console.error('Fel vid hämtning av aktiviteter:', error);
    }
  };

  // Hämta fullständigt e-postinnehåll
  const fetchEmailDetails = async (emailId) => {
    try {
      const response = await axios.get(`${API_URL}/emails/${emailId}`, config);
      setSelectedEmail(response.data.data);
    } catch (error) {
      console.error('Fel vid hämtning av e-post:', error);
    }
  };

  // Toggla mejlspårning för en deal
  const toggleEmailTracking = async (dealId, enabled) => {
    try {
      await axios.put(`${API_URL}/deals/${dealId}/email-tracking`, { enabled }, config);
      // Uppdatera selectedDeal lokalt
      setSelectedDeal(prev => ({
        ...prev,
        emailTrackingDisabled: !enabled
      }));
      setSuccess(enabled ? 'E-postspårning aktiverad' : 'E-postspårning inaktiverad');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Kunde inte ändra mejlspårning');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Hämta omatchade mejl
  const fetchUnmatchedEmails = async () => {
    try {
      const response = await axios.get(`${API_URL}/emails/unmatched?limit=50`, config);
      setUnmatchedEmails(response.data.data || []);
      setUnmatchedCount(response.data.total || 0);
    } catch (error) {
      console.error('Fel vid hämtning av omatchade mejl:', error);
    }
  };

  // Skapa lead från omatchat mejl
  const createLeadFromEmail = async (emailId, createDeal = false) => {
    setCreatingLead(emailId);
    try {
      const response = await axios.post(`${API_URL}/emails/${emailId}/create-lead`, {
        createDeal: createDeal
      }, config);

      setSuccess(response.data.message);
      setTimeout(() => setSuccess(''), 3000);

      // Ta bort mejlet från listan
      setUnmatchedEmails(prev => prev.filter(e => e.id !== emailId));
      setUnmatchedCount(prev => prev - 1);

      // Uppdatera pipeline om deal skapades
      if (createDeal) {
        fetchPipeline();
      }

    } catch (error) {
      setError(error.response?.data?.error || 'Kunde inte skapa lead');
      setTimeout(() => setError(''), 3000);
    } finally {
      setCreatingLead(null);
    }
  };

  // Generera AI-sammanfattning för deal
  const generateAiSummary = async (dealId) => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API_URL}/deals/${dealId}/summarize`, {}, config);
      setAiSummary(response.data.data);
      setSuccess('AI-sammanfattning genererad');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Fel vid AI-sammanfattning:', error);
      setError(error.response?.data?.error || 'Kunde inte generera sammanfattning');
      setTimeout(() => setError(''), 3000);
    } finally {
      setAiLoading(false);
    }
  };

  // Toggla att-göra-punkt
  const toggleActionItem = async (dealId, index, done) => {
    try {
      const response = await axios.put(
        `${API_URL}/deals/${dealId}/action-items/${index}`,
        { done },
        config
      );
      // Uppdatera lokalt state
      setAiSummary(prev => ({
        ...prev,
        actionItems: response.data.data.actionItems
      }));
    } catch (error) {
      console.error('Fel vid uppdatering av att-göra:', error);
    }
  };

  // ========== EMAIL FUNCTIONS ==========

  const openEmailModal = () => {
    // Pre-fill with contact email if available
    const contactEmail = selectedDeal?.contact?.email || '';
    setEmailFormData({
      to: contactEmail,
      subject: `Angående: ${selectedDeal?.name || ''}`,
      body: ''
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailFormData.to || !emailFormData.subject || !emailFormData.body) {
      setError('Fyll i alla fält');
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API_URL}/deals/${selectedDeal.id}/send-email`, emailFormData, config);
      setSuccess('E-post skickad!');
      setTimeout(() => setSuccess(''), 3000);
      setShowEmailModal(false);
      setEmailFormData({ to: '', subject: '', body: '' });
      // Refresh deal activities
      if (selectedDeal) {
        fetchDealActivities(selectedDeal.id);
      }
    } catch (error) {
      setError('Kunde inte skicka e-post: ' + (error.response?.data?.message || error.message));
      setTimeout(() => setError(''), 5000);
    }
    setSendingEmail(false);
  };

  // ========== FILTER FUNCTIONS ==========

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchPipeline(filters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      assignedToId: '',
      companyId: '',
      minValue: '',
      maxValue: '',
      minProbability: '',
      search: ''
    };
    setFilters(emptyFilters);
    fetchPipeline(emptyFilters);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  // ========== BULK OPERATIONS ==========

  const toggleDealSelection = (dealId, event) => {
    event.stopPropagation(); // Don't trigger deal view
    setSelectedDeals(prev =>
      prev.includes(dealId)
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const selectAllInStage = (stageId) => {
    const stage = pipelineData.find(s => s.id === stageId);
    if (!stage) return;
    const dealIds = stage.deals?.map(d => d.id) || [];
    setSelectedDeals(prev => {
      const allSelected = dealIds.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !dealIds.includes(id));
      } else {
        return [...new Set([...prev, ...dealIds])];
      }
    });
  };

  const clearSelection = () => {
    setSelectedDeals([]);
  };

  const handleBulkMove = async (targetStageId) => {
    if (selectedDeals.length === 0) return;
    setBulkActionInProgress(true);
    try {
      await axios.put(`${API_URL}/deals/bulk-move`, {
        dealIds: selectedDeals,
        stageId: targetStageId
      }, config);
      setSuccess(`${selectedDeals.length} deals flyttade`);
      setTimeout(() => setSuccess(''), 3000);
      fetchPipeline();
    } catch (error) {
      setError('Kunde inte flytta deals: ' + (error.response?.data?.message || error.message));
      setTimeout(() => setError(''), 5000);
    }
    setBulkActionInProgress(false);
  };

  const handleBulkAssign = async (userId) => {
    if (selectedDeals.length === 0) return;
    setBulkActionInProgress(true);
    try {
      await axios.put(`${API_URL}/deals/bulk-assign`, {
        dealIds: selectedDeals,
        assignedToId: userId || null
      }, config);
      setSuccess(`${selectedDeals.length} deals tilldelade`);
      setTimeout(() => setSuccess(''), 3000);
      fetchPipeline();
    } catch (error) {
      setError('Kunde inte tilldela deals: ' + (error.response?.data?.message || error.message));
      setTimeout(() => setError(''), 5000);
    }
    setBulkActionInProgress(false);
  };

  const handleBulkDelete = async () => {
    if (selectedDeals.length === 0) return;
    if (!window.confirm(`Är du säker på att du vill ta bort ${selectedDeals.length} deals? Detta går inte att ångra.`)) {
      return;
    }
    setBulkActionInProgress(true);
    try {
      await axios.delete(`${API_URL}/deals/bulk`, {
        ...config,
        data: { dealIds: selectedDeals }
      });
      setSuccess(`${selectedDeals.length} deals borttagna`);
      setTimeout(() => setSuccess(''), 3000);
      fetchPipeline();
    } catch (error) {
      setError('Kunde inte ta bort deals: ' + (error.response?.data?.message || error.message));
      setTimeout(() => setError(''), 5000);
    }
    setBulkActionInProgress(false);
  };

  // Hantera drag start
  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('dragging');
  };

  // Hantera drag end
  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  // Hantera drag over
  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  // Hantera drop
  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!draggedDeal || draggedDeal.stageId === targetStageId) return;

    try {
      await axios.put(`${API_URL}/deals/${draggedDeal.id}/stage`,
        { stageId: targetStageId },
        config
      );
      setSuccess('Deal flyttad');
      fetchPipeline();
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte flytta deal');
    }
  };

  // Öppna deal-detaljer — hämta allt i ett anrop (activities + emails inkluderade)
  const handleViewDeal = async (deal) => {
    setSelectedDeal(deal);
    setDealEmails([]);
    setDealActivities([]);
    setSelectedEmail(null);
    setAiSummary(null);
    try {
      const response = await axios.get(`${API_URL}/deals/${deal.id}`, config);
      const fullDeal = response.data.data;
      setSelectedDeal(fullDeal);
      setDealActivities(fullDeal.activities || []);
      setDealEmails(fullDeal.emails || []);
      if (fullDeal.aiSummary || fullDeal.aiActionItems) {
        setAiSummary({
          summary: fullDeal.aiSummary,
          actionItems: fullDeal.aiActionItems || [],
          nextStep: fullDeal.aiNextStep,
          updatedAt: fullDeal.aiSummaryUpdatedAt
        });
      }
    } catch (error) {
      console.error('Fel vid hämtning av deal-detaljer:', error);
      // Fallback till card-data
      if (deal.aiSummary || deal.aiActionItems) {
        setAiSummary({
          summary: deal.aiSummary,
          actionItems: deal.aiActionItems || [],
          nextStep: deal.aiNextStep,
          updatedAt: deal.aiSummaryUpdatedAt
        });
      }
    }
  };

  // Redigera deal
  const handleEditDeal = (deal) => {
    ensureFormData();
    setEditingDeal(deal);
    setFormData({
      name: deal.name || '',
      b2bCompanyId: deal.b2bCompanyId || '',
      contactId: deal.contactId || '',
      stageId: deal.stageId || '',
      courseType: deal.courseType || 'custom',
      customCourseDescription: deal.customCourseDescription || '',
      estimatedParticipants: deal.estimatedParticipants || '',
      preferredLocation: deal.preferredLocation || '',
      preferredDates: deal.preferredDates || '',
      expectedValue: deal.expectedValue || '',
      probability: deal.probability || 50,
      expectedCloseDate: deal.expectedCloseDate || '',
      source: deal.source || '',
      notes: deal.notes || ''
    });
    setShowDealForm(true);
  };

  // Spara deal
  const handleSubmitDeal = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Deal-namn krävs');
      return;
    }

    if (!formData.stageId && !editingDeal) {
      setError('Välj ett pipeline-steg');
      return;
    }

    try {
      const submitData = {
        ...formData,
        stageId: formData.stageId || pipelineData[0]?.id,
        b2bCompanyId: formData.b2bCompanyId || null,
        contactId: formData.contactId || null,
        estimatedParticipants: formData.estimatedParticipants || null,
        expectedValue: formData.expectedValue || null,
        expectedCloseDate: formData.expectedCloseDate || null
      };

      if (editingDeal) {
        await axios.put(`${API_URL}/deals/${editingDeal.id}`, submitData, config);
        setSuccess('Deal uppdaterad');
      } else {
        await axios.post(`${API_URL}/deals`, submitData, config);
        setSuccess('Deal skapad');
      }

      setShowDealForm(false);
      setEditingDeal(null);
      resetDealForm();
      fetchPipeline();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte spara deal');
    }
  };

  // Ta bort deal
  const handleDeleteDeal = async (id) => {
    if (!window.confirm('Vill du verkligen ta bort denna deal?')) return;

    try {
      await axios.delete(`${API_URL}/deals/${id}`, config);
      setSuccess('Deal borttagen');
      if (selectedDeal?.id === id) {
        setSelectedDeal(null);
      }
      fetchPipeline();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte ta bort deal');
    }
  };

  // Lägg till aktivitet
  const handleSubmitActivity = async (e) => {
    e.preventDefault();
    if (!selectedDeal) return;

    try {
      await axios.post(`${API_URL}/deals/${selectedDeal.id}/activities`, activityFormData, config);
      setSuccess('Aktivitet tillagd');
      setShowActivityForm(false);
      setActivityFormData({ activityTypeId: '', subject: '', description: '', scheduledAt: '' });
      fetchDealActivities(selectedDeal.id);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte lägga till aktivitet');
    }
  };

  // Markera som vunnen/förlorad
  const handleMarkWon = async () => {
    if (!selectedDeal) return;
    const wonStage = pipelineData.find(s => s.isWonStage);
    if (wonStage) {
      try {
        await axios.put(`${API_URL}/deals/${selectedDeal.id}/stage`, { stageId: wonStage.id }, config);
        setSuccess('Deal markerad som vunnen!');
        fetchPipeline();
        setSelectedDeal(null);
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Kunde inte markera som vunnen');
      }
    }
  };

  const handleMarkLost = async () => {
    if (!selectedDeal) return;
    const lostReason = window.prompt('Ange anledning till förlorad deal (valfritt):');
    const lostStage = pipelineData.find(s => s.isLostStage);
    if (lostStage) {
      try {
        await axios.put(`${API_URL}/deals/${selectedDeal.id}`, {
          stageId: lostStage.id,
          lostReason,
          lostAt: new Date().toISOString()
        }, config);
        setSuccess('Deal markerad som förlorad');
        fetchPipeline();
        setSelectedDeal(null);
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Kunde inte markera som förlorad');
      }
    }
  };

  // Återställ formulär
  const resetDealForm = () => {
    setFormData({
      name: '',
      b2bCompanyId: '',
      contactId: '',
      stageId: '',
      courseType: 'custom',
      customCourseDescription: '',
      estimatedParticipants: '',
      preferredLocation: '',
      preferredDates: '',
      expectedValue: '',
      probability: 50,
      expectedCloseDate: '',
      source: '',
      notes: ''
    });
  };

  // Formatera pengar
  const formatCurrency = (value) => {
    if (!value) return '0 kr';
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Formatera datum
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <main className="main-content">
        <LoadingSpinner message="Laddar pipeline..." />
      </main>
    );
  }

  // Beräkna totalsummor
  const totalValue = pipelineData
    .filter(s => !s.isWonStage && !s.isLostStage)
    .reduce((sum, s) => sum + (s.totalValue || 0), 0);
  const totalWeighted = pipelineData
    .filter(s => !s.isWonStage && !s.isLostStage)
    .reduce((sum, s) => sum + (s.weightedValue || 0), 0);

  return (
    <main className="main-content pipeline-page">
      <div className="page-header">
        <div className="header-left">
          <h2>Säljpipeline</h2>
          <div className="pipeline-stats">
            <div className="stat">
              <span className="stat-label">Totalt värde:</span>
              <span className="stat-value">{formatCurrency(totalValue)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Viktat värde:</span>
              <span className="stat-value">{formatCurrency(totalWeighted)}</span>
            </div>
          </div>
        </div>
        <div className="header-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => {
              fetchUnmatchedEmails();
              setShowUnmatchedEmails(true);
            }}
          >
            Omatchade mejl {unmatchedCount > 0 && <span className="badge">{unmatchedCount}</span>}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetDealForm();
              setEditingDeal(null);
              if (pipelineData.length > 0) {
                setFormData(prev => ({ ...prev, stageId: pipelineData[0].id }));
              }
              ensureFormData();
              setShowDealForm(true);
            }}
          >
            + Ny affärsmöjlighet
          </button>
        </div>
      </div>

      <AlertMessage error={error} success={success} />

      {/* Filter bar */}
      <div className="filter-bar">
        <button
          className={`btn btn-small ${showFilters ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { if (!showFilters && companies.length === 0) ensureFormData(); setShowFilters(!showFilters); }}
        >
          Filter {hasActiveFilters && <span className="filter-badge">!</span>}
        </button>

        {showFilters && (
          <div className="filter-controls">
            <input
              type="text"
              placeholder="Sök deal..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="filter-input"
            />
            <select
              value={filters.assignedToId}
              onChange={(e) => handleFilterChange('assignedToId', e.target.value)}
              className="filter-select"
            >
              <option value="">Alla ansvariga</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
            <select
              value={filters.companyId}
              onChange={(e) => handleFilterChange('companyId', e.target.value)}
              className="filter-select"
            >
              <option value="">Alla företag</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Min värde"
              value={filters.minValue}
              onChange={(e) => handleFilterChange('minValue', e.target.value)}
              className="filter-input filter-small"
            />
            <input
              type="number"
              placeholder="Max värde"
              value={filters.maxValue}
              onChange={(e) => handleFilterChange('maxValue', e.target.value)}
              className="filter-input filter-small"
            />
            <input
              type="number"
              placeholder="Min %"
              value={filters.minProbability}
              onChange={(e) => handleFilterChange('minProbability', e.target.value)}
              className="filter-input filter-tiny"
              min="0"
              max="100"
            />
            <button className="btn btn-small btn-primary" onClick={applyFilters}>
              Tillämpa
            </button>
            {hasActiveFilters && (
              <button className="btn btn-small btn-secondary" onClick={clearFilters}>
                Rensa
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {selectedDeals.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">{selectedDeals.length} valda</span>
          <select
            onChange={(e) => {
              if (e.target.value) handleBulkMove(parseInt(e.target.value));
              e.target.value = '';
            }}
            disabled={bulkActionInProgress}
            className="bulk-select"
          >
            <option value="">Flytta till...</option>
            {pipelineData.map(stage => (
              <option key={stage.id} value={stage.id}>{stage.name}</option>
            ))}
          </select>
          <select
            onChange={(e) => {
              handleBulkAssign(e.target.value ? parseInt(e.target.value) : null);
              e.target.value = '';
            }}
            disabled={bulkActionInProgress}
            className="bulk-select"
          >
            <option value="">Tilldela till...</option>
            <option value="">Ingen</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
            ))}
          </select>
          <button
            className="btn btn-small btn-danger"
            onClick={handleBulkDelete}
            disabled={bulkActionInProgress}
          >
            Ta bort
          </button>
          <button
            className="btn btn-small btn-secondary"
            onClick={clearSelection}
            disabled={bulkActionInProgress}
          >
            Avmarkera
          </button>
        </div>
      )}

      <div className="pipeline-container">
        <PipelineBoard
          pipelineData={pipelineData}
          selectedDeals={selectedDeals}
          dragOverStage={dragOverStage}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onViewDeal={handleViewDeal}
          onToggleDealSelection={toggleDealSelection}
          onSelectAllInStage={selectAllInStage}
          setDragOverStage={setDragOverStage}
        />

        <DealDetailPanel
          deal={selectedDeal}
          dealEmails={dealEmails}
          dealActivities={dealActivities}
          aiSummary={aiSummary}
          aiLoading={aiLoading}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          formatDateTime={formatDateTime}
          onClose={() => setSelectedDeal(null)}
          onMarkWon={handleMarkWon}
          onMarkLost={handleMarkLost}
          onEmail={openEmailModal}
          onEdit={handleEditDeal}
          onDelete={handleDeleteDeal}
          onToggleEmailTracking={toggleEmailTracking}
          onFetchEmailDetails={fetchEmailDetails}
          onGenerateAi={generateAiSummary}
          onToggleActionItem={toggleActionItem}
          onAddActivity={() => setShowActivityForm(true)}
        />
      </div>

      <DealFormModal
        show={showDealForm}
        editing={editingDeal}
        form={formData}
        setForm={setFormData}
        companies={companies}
        contacts={contacts}
        pipelineData={pipelineData}
        onSubmit={handleSubmitDeal}
        onClose={() => setShowDealForm(false)}
      />

      <ActivityFormModal
        show={showActivityForm}
        form={activityFormData}
        setForm={setActivityFormData}
        activityTypes={activityTypes}
        onSubmit={handleSubmitActivity}
        onClose={() => setShowActivityForm(false)}
      />

      <EmailDetailModal
        email={selectedEmail}
        onClose={() => setSelectedEmail(null)}
      />

      <UnmatchedEmailsModal
        show={showUnmatchedEmails}
        emails={unmatchedEmails}
        count={unmatchedCount}
        creatingLead={creatingLead}
        onCreateLead={createLeadFromEmail}
        onClose={() => setShowUnmatchedEmails(false)}
      />

      <EmailComposeModal
        show={showEmailModal}
        form={emailFormData}
        setForm={setEmailFormData}
        sending={sendingEmail}
        onSend={handleSendEmail}
        onClose={() => setShowEmailModal(false)}
      />
    </main>
  );
}

export default Pipeline;
